import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpResponse } from '@angular/common/http';
import { TwoFaAuthenticationDataService } from '@services/user/two-fa-authentication.data.service';
import {
	AvailableQuestion,
	SecretQuestions,
	Step,
	Steps,
	TwoFaSetupData,
	User2FASettings
} from '@models/two-fa.models';
import { finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from '@services/helpers/message-service/message.service';
import { ConfirmGeneralModalComponent } from '@web-components/common/confirm-general-modal/confirm-general-modal.component';
import { SNModalService } from '@web-services/modal/snModal.service';
import { TwoFaAuthenticationService } from '@services/user/two-fa-authentication.service';

@Component({
	selector: 'two-fa-setup',
	template: require('./two-fa-setup-modal.html'),
	styles: [require('./two-fa-setup-modal.scss')]
})
export class TwoFaSetupModalComponent implements OnInit, OnDestroy {
	@Input() userTwoFASettings: User2FASettings;
	@Input() primaryAction: string;
	@Input() enforce?: boolean;

	steps: Array<Step> = Steps;
	questions: Array<AvailableQuestion> = JSON.parse(
		JSON.stringify(SecretQuestions)
	);
	activeStep = 0;
	QRImage: string;
	dataToSave: TwoFaSetupData = {
		sixDigitVerificationCode: '',
		secretQuestions: [
			{ question: null, answer: '' },
			{ question: null, answer: '' },
			{ question: null, answer: '' }
		],
		enforce2FA: false
	};
	buttonDisable: boolean;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private activeModal: NgbActiveModal,
		private twoFaAuthenticationDataService: TwoFaAuthenticationDataService,
		private twoFaAuthenticationService: TwoFaAuthenticationService,
		private messageService: MessageService,
		private modalService: SNModalService
	) {}

	ngOnInit(): void {
		this.parseQRImage(
			this.userTwoFASettings.configure2faMetadata.actions.DOWNLOAD_QR_IMAGE.url
		);

		if (this.enforce) {
			this.dataToSave.enforce2FA = !!this.enforce;
		}
	}

	ngOnDestroy(): void {
		this.questions = [];
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	saveAndClose(): void {
		if (
			this.twoFaAuthenticationService.isTwoFaSetupValid(
				this.dataToSave,
				this.setStep.bind(this)
			)
		) {
			this.buttonDisable = true;

			const { url, method } = this.userTwoFASettings.actions.TURN_ON_2FA;
			this.twoFaAuthenticationDataService
				.setup2FaAuthentication(url, method, this.dataToSave)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize((): boolean => (this.buttonDisable = false))
				)
				.subscribe(
					(): void => {
						this.twoFaAuthenticationDataService.setTwoFaModalControlState({
							state: true,
							action: 'SUCCESS_SETUP',
							payload: { enforce: this.enforce }
						});
						this.messageService.showToaster('s_0', 'twoFa');
						this.activeModal.close(this.primaryAction);
						this.twoFaAuthenticationDataService.setTwoFaModalControlState(null);
					},
					({ error }): void => {
						const { errorMessage, errorTitle } = error;
						this.messageService.showErrorFromBackend(errorTitle, errorMessage);
					}
				);
		}
	}

	onCodeEnter(code: string): void {
		this.dataToSave.sixDigitVerificationCode = code;
	}

	nextStep(): void {
		this.activeStep++;
	}

	previousStep(): void {
		this.activeStep--;
	}

	setStep(step: number): void {
		this.activeStep = step;
	}

	onDismiss(): void {
		if (!this.buttonDisable) {
			this.confirmModalClose();
		}
	}

	private parseQRImage(url: string): void {
		this.twoFaAuthenticationDataService
			.getQRUrl(url)
			.subscribe((data: HttpResponse<Blob>): void => {
				const reader = new FileReader();
				reader.readAsDataURL(data.body);
				reader.onloadend = (): void => {
					this.QRImage = reader.result;
				};
			});
	}

	private confirmModalClose(): void {
		const confirmModal = this.modalService.openModal(
			ConfirmGeneralModalComponent,
			{
				windowClass: 'ng-bootstrap-modal modal-visible modal-narrow'
			}
		);
		confirmModal.componentInstance.text =
			'Two-factor authentication setup is not yet complete. Are you sure?';
		confirmModal.componentInstance.buttons = [
			{
				text: 'Yes, cancel setup',
				color: 'danger',
				size: 'lg',
				action: 'CANCEL_2FA_SETUP'
			},
			{
				text: 'No, go back',
				color: 'success',
				size: 'lg',
				action: 'PROCEED_2FA_SETUP'
			}
		];

		confirmModal.result.then((action: string): void => {
			if (action === 'CANCEL_2FA_SETUP') {
				this.activeModal.close('DISMISS_SETUP');
				this.twoFaAuthenticationDataService.setTwoFaModalControlState({
					state: true,
					action: 'DISMISS_SETUP',
					payload: { enforce: this.enforce }
				});
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/2fa/modals/two-fa-setup-modal/two-fa-setup-modal.ts